import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Navigation } from 'app/core/navigation/navigation.types';
import { Observable, ReplaySubject, tap, map } from 'rxjs';
import { FuseNavigationItem } from '@fuse/components/navigation';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _httpClient = inject(HttpClient);
    private _navigation: ReplaySubject<Navigation> =
        new ReplaySubject<Navigation>(1);

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for navigation
     */
    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all navigation data
     */
    get(): Observable<Navigation> {
        return this._httpClient.get<Navigation>('api/common/navigation').pipe(
            map((navigation) => this._filterNavigationByRole(navigation)),
            tap((navigation) => {
                this._navigation.next(navigation);
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Filter navigation items based on user role
     */
    private _filterNavigationByRole(navigation: Navigation): Navigation {
        // Get user data from localStorage
        let userRole: string | null = null;
        let hasEmailToken: boolean = false;
        let productionLandingPage: string | null = null;
        let userData: any = null;
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                userData = JSON.parse(userDataString);
                userRole = userData?.role;
                hasEmailToken = !!userData?.email_token;
                // For Production users, get their assigned work page
                if (userRole === 'Production') {
                    productionLandingPage = userData?.default_landing_page || userData?.defaultLandingPage || null;
                }
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }

        // If no role found, return navigation as is
        if (!userRole) {
            return navigation;
        }

        // Filter each navigation type
        // For horizontal navigation (modern layout), Admin users get a limited set of pages
        return {
            compact: this._filterNavigationItems(navigation.compact, userRole, hasEmailToken, productionLandingPage, false, userData),
            default: this._filterNavigationItems(navigation.default, userRole, hasEmailToken, productionLandingPage, false, userData),
            futuristic: this._filterNavigationItems(navigation.futuristic, userRole, hasEmailToken, productionLandingPage, false, userData),
            horizontal: this._filterNavigationItems(navigation.horizontal, userRole, hasEmailToken, productionLandingPage, true, userData),
        };
    }

    /**
     * Recursively filter navigation items based on user role and email token
     * For Production users, dynamically show their assigned work page
     * For Admin users in horizontal nav (modern layout), show limited pages
     */
    private _filterNavigationItems(
        items: FuseNavigationItem[],
        userRole: string,
        hasEmailToken: boolean,
        productionLandingPage: string | null = null,
        isHorizontalNav: boolean = false,
        userData: any = null
    ): FuseNavigationItem[] {
        return items
            .filter((item) => {
                // Hide mailbox if user doesn't have email_token
                if (item.id === 'mailbox' && !hasEmailToken) {
                    return false;
                }

                // Special handling for Production users - they get their assigned page dynamically
                if (userRole === 'Production') {
                    return this._isItemVisibleForProductionUser(item, productionLandingPage);
                }

                // Special handling for Admin users in horizontal navigation (modern layout)
                // Show only: New Order, Order List, Mailbox, Chat, Users, Gallery
                if (userRole === 'Admin' && isHorizontalNav) {
                    return this._isItemVisibleForAdminInModern(item);
                }

                // Special handling for Trader users in horizontal navigation (modern layout)
                // Show only: New Order, Order List, Chat, Customers, Gallery, Printers
                if ((userRole === 'Trader' || !!userData?.operates_as_trader) && isHorizontalNav) {
                    return this._isItemVisibleForTraderInModern(item);
                }

                // Special handling for Sales users in horizontal navigation (modern layout)
                // Show only: New Order, Order List, Mailbox, Chat, Customers, Gallery (NO Resources)
                // Assistants under Ind. Sales use the Sales nav (no printers).
                if (
                    (userRole === 'Sales' || userRole === 'Assistant') &&
                    !userData?.operates_as_trader &&
                    isHorizontalNav
                ) {
                    return this._isItemVisibleForSalesInModern(item);
                }

                // If item has meta.roles defined, check if user role is in the list
                if (item.meta && item.meta['roles']) {
                    const allowedRoles = item.meta['roles'] as string[];
                    if (allowedRoles.includes(userRole)) {
                        return true;
                    }
                    // Assistants inherit parent org nav roles
                    if (userRole === 'Assistant' && userData?.operates_as_trader && allowedRoles.includes('Trader')) {
                        return true;
                    }
                    if (userRole === 'Assistant' && !userData?.operates_as_trader && allowedRoles.includes('Sales')) {
                        return true;
                    }
                    return false;
                }
                // If no roles specified, item is visible to all
                return true;
            })
            .map((item) => {
                // TEMPORARY (modern layout only): re-route the Mailbox nav
                // entry into the orders list workspace so we can dogfood
                // the new `app-email-workspace` component without ripping
                // out the legacy `/mailbox` route. The query param is
                // picked up by `OrderListComponent` (see
                // `_watchEmailWorkspaceQueryParam`) which calls
                // `openEmailInWorkspace()`. Vertical / classic layouts
                // continue pointing at `/mailbox` because they don't
                // host the orders list workspace at all (see
                // `OrderListComponent.workspaceEnabled`).
                if (isHorizontalNav && item.id === 'mailbox') {
                    item = {
                        ...item,
                        link: '/orders/list',
                        queryParams: { workspace: 'email' },
                    };
                }

                // TEMPORARY (modern layout only): re-route the Chat nav
                // entry into the orders list workspace so the new
                // `app-chat-workspace` component is the modern default
                // without ripping out the legacy `/chat` route. Same
                // mechanism as Mailbox above — the query param is
                // picked up by `OrderListComponent._watchChatWorkspaceQueryParam`
                // which calls `openChatInWorkspace()`. Vertical /
                // classic layouts continue pointing at `/chat` because
                // they don't host the orders list workspace (gated by
                // `OrderListComponent.workspaceEnabled`).
                if (isHorizontalNav && item.id === 'chat') {
                    item = {
                        ...item,
                        link: '/orders/list',
                        queryParams: { workspace: 'chat' },
                    };
                }

                // Recursively filter children if they exist
                if (item.children && item.children.length > 0) {
                    return {
                        ...item,
                        children: this._filterNavigationItems(item.children, userRole, hasEmailToken, productionLandingPage, isHorizontalNav, userData),
                    };
                }
                return item;
            });
    }

    /**
     * Check if a navigation item should be visible for Production users
     * Production users get: limited default pages (gallery, chat, mailbox) + their ONE assigned work page
     * Note: Orders (add-order, order-list) removed - Production users only access orders through their assigned work page if applicable
     */
    private _isItemVisibleForProductionUser(item: FuseNavigationItem, assignedPage: string | null): boolean {
        // Default pages always visible for Production users (limited set)
        const defaultProductionPages = [
            'image-gallery',
            'chat',
            'mailbox',
            'shopify',
            // Note: add-order and order-list removed - Production users don't need direct order access
            // Note: Resources removed - Production users only see it if assigned as their work page
        ];

        if (defaultProductionPages.includes(item.id)) {
            return true;
        }

        // Production users ONLY see default pages + their ONE assigned work page
        // We don't check meta.roles here - that's for other user types

        // Show the assigned work page if it matches this item's link
        if (assignedPage && item.link) {
            // Normalize links for comparison (remove leading slash if present)
            const normalizedAssigned = assignedPage.startsWith('/') ? assignedPage : '/' + assignedPage;
            const normalizedItemLink = item.link.startsWith('/') ? item.link : '/' + item.link;
            
            if (normalizedAssigned === normalizedItemLink) {
                return true;
            }
        }

        return false;
    }

    /**
     * Check if a navigation item should be visible for Admin users in modern/horizontal layout
     * Admin users in modern layout get: Operations, Order List, Products, CRM, Users (basic link), Gallery
     *
     * NOTE: 'mailbox' and 'chat' are intentionally EXCLUDED. Both workspaces
     * live inside `/orders/list`'s right panel and are switched via the
     * workspace chips at the top of the orders filter sidebar. The
     * cross-page nav now reads cleaner without those redundant entries.
     * NOTE: 'add-order' is intentionally excluded — modern layout opens
     * the new order form inside the orders list workspace via the
     * "New Order" icon in the filter bar.
     */
    private _isItemVisibleForAdminInModern(item: FuseNavigationItem): boolean {
        // Pages visible for Admin in modern/horizontal layout
        // Note: 'admin-users' is a basic link (not collapsible), better for horizontal nav
        const adminModernPages = [
            'dashboard',      // Operations
            'order-list',     // Order List
            'products',       // Products
            'shopify',        // Shopify
            'crm',            // CRM
            'admin-users',    // Users (basic link for horizontal nav - goes to /user-list)
            'image-gallery',  // Gallery
        ];

        return adminModernPages.includes(item.id);
    }

    /**
     * Check if a navigation item should be visible for Trader users in modern/horizontal layout
     * Trader users in modern layout get: Order List, Products, CRM, Customers, Gallery, Printers
     *
     * NOTE: 'mailbox' and 'chat' are intentionally EXCLUDED — see the
     * Admin variant above for the rationale (workspace chips in the
     * orders filter sidebar replace those nav entries).
     * NOTE: 'add-order' is intentionally excluded — handled by the orders list workspace.
     */
    private _isItemVisibleForTraderInModern(item: FuseNavigationItem): boolean {
        // Pages visible for Trader in modern/horizontal layout
        const traderModernPages = [
            'order-list',       // Order List
            'products',         // Products
            'shopify',          // Shopify
            'crm',              // CRM
            'customers',        // Customers
            'image-gallery',    // Gallery
            'trader-printers',  // Printers
        ];

        return traderModernPages.includes(item.id);
    }

    /**
     * Check if a navigation item should be visible for Sales users in modern/horizontal layout
     * Sales users in modern layout get: Order List, Products, CRM, Customers, Gallery
     *
     * NOTE: 'mailbox' and 'chat' are intentionally EXCLUDED — see the
     * Admin variant above for the rationale (workspace chips in the
     * orders filter sidebar replace those nav entries).
     * NOTE: Resources page is hidden for Sales users (Admin only).
     * NOTE: 'add-order' is intentionally excluded — handled by the orders list workspace.
     */
    private _isItemVisibleForSalesInModern(item: FuseNavigationItem): boolean {
        // Pages visible for Sales in modern/horizontal layout
        const salesModernPages = [
            'order-list',       // Order List
            'products',         // Products
            'shopify',          // Shopify
            'crm',              // CRM
            'customers',        // Customers
            'image-gallery',    // Gallery
            // NOTE: 'resources' is intentionally excluded - Admin only
        ];

        return salesModernPages.includes(item.id);
    }
}
